import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Crown, ArrowRight, X, Sparkles } from 'lucide-react';
import { Button } from './ui/button';
import { useAuth, SHOW_PRICING } from '../App';

// ============ ELEVATION MOMENT COMPONENT ============
// Reusable component for in-app upgrade moments
// Shows different content for Essentiel vs Experience users
// Hidden when SHOW_PRICING is false (Beta mode)

const ElevationMoment = ({ 
  trigger, // 'content_generation' | 'dashboard_reminder'
  onClose,
  className = ''
}) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [stage, setStage] = useState(0);
  
  // During Beta (SHOW_PRICING=false), don't show any upgrade prompts
  if (!SHOW_PRICING) {
    return null;
  }
  
  const isEssentiel = user?.subscription_tier === 'essentiel' || 
                      user?.subscription_tier === 'free' || 
                      !user?.subscription_tier;

  const handleUpgrade = () => {
    navigate(`/pricing?plan=experience&source=${trigger}`);
    onClose?.();
  };

  const handleContinue = () => {
    onClose?.();
  };

  // Content Generation Moment - After user generates content
  if (trigger === 'content_generation') {
    if (isEssentiel) {
      return (
        <AnimatePresence>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className={`fixed inset-0 z-50 bg-black/95 flex items-center justify-center p-6 ${className}`}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ delay: 0.1 }}
              className="max-w-lg w-full text-center relative"
            >
              {/* Close button */}
              <button
                onClick={handleContinue}
                className="absolute top-0 right-0 text-[#52525B] hover:text-[#71717A] transition-colors"
              >
                <X size={20} />
              </button>

              {/* Content */}
              <motion.p
                initial={{ y: -10, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ delay: 0.2 }}
                className="text-white text-xl mb-2"
                style={{ fontFamily: "'Playfair Display', serif" }}
              >
                Tu viens de créer du contenu.
              </motion.p>
              
              <motion.p
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.4 }}
                className="text-[#A1A1AA] text-base mb-6"
              >
                Mais imagine si chaque idée était…
              </motion.p>

              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.6 }}
                className="space-y-2 mb-6"
              >
                <p className="text-[#D4AF37] text-base">illimitée.</p>
                <p className="text-[#D4AF37] text-base">toujours alignée.</p>
                <p className="text-[#D4AF37] text-base">pensée pour toi.</p>
              </motion.div>

              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.8 }}
                className="w-8 h-px bg-[#27272A] mx-auto my-6"
              />

              <motion.p
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 1.0 }}
                className="text-white text-sm mb-8"
                style={{ fontFamily: "'Playfair Display', serif" }}
              >
                C'est ce que Vision Lab Expérience te donne.
              </motion.p>

              {/* CTAs */}
              <motion.div
                initial={{ y: 10, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ delay: 1.2 }}
                className="flex flex-col items-center gap-3"
              >
                <Button
                  onClick={handleUpgrade}
                  className="bg-transparent border border-[#D4AF37]/40 text-[#D4AF37] hover:bg-[#D4AF37]/5 px-6 py-4"
                >
                  <Crown className="mr-2" size={16} />
                  Découvrir Vision Lab Expérience
                </Button>
                
                <button
                  onClick={handleContinue}
                  className="text-[#52525B] text-sm hover:text-[#71717A] transition-colors"
                >
                  Continuer →
                </button>
              </motion.div>
            </motion.div>
          </motion.div>
        </AnimatePresence>
      );
    } else {
      // Experience user - Activation message
      return (
        <AnimatePresence>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className={`fixed inset-0 z-50 bg-black/95 flex items-center justify-center p-6 ${className}`}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ delay: 0.1 }}
              className="max-w-lg w-full text-center relative"
            >
              {/* Badge */}
              <motion.div
                initial={{ scale: 0.9, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={{ delay: 0.2 }}
                className="flex items-center justify-center gap-2 mb-6"
              >
                <div className="w-6 h-6 bg-[#D4AF37]/10 border border-[#D4AF37]/30 rounded-full flex items-center justify-center">
                  <Sparkles className="text-[#D4AF37]" size={12} />
                </div>
                <p className="text-[#D4AF37]/60 text-xs tracking-[0.2em] uppercase">
                  Vision Lab Expérience
                </p>
              </motion.div>

              <motion.p
                initial={{ y: -10, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ delay: 0.3 }}
                className="text-white text-xl mb-6"
                style={{ fontFamily: "'Playfair Display', serif" }}
              >
                Encore un contenu aligné.
              </motion.p>
              
              <motion.p
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.5 }}
                className="text-[#A1A1AA] text-base mb-2"
              >
                Tu ne cherches plus.
              </motion.p>
              
              <motion.p
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.7 }}
                className="text-[#D4AF37] text-base italic mb-8"
              >
                Tu crées.
              </motion.p>

              <motion.div
                initial={{ y: 10, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ delay: 0.9 }}
              >
                <Button
                  onClick={handleContinue}
                  className="bg-[#D4AF37] hover:bg-[#B8860B] text-black px-6 py-4"
                >
                  Continuer <ArrowRight className="ml-2" size={16} />
                </Button>
              </motion.div>
            </motion.div>
          </motion.div>
        </AnimatePresence>
      );
    }
  }

  // Dashboard Reminder - Subtle card for users after a few days
  if (trigger === 'dashboard_reminder') {
    if (!isEssentiel) return null; // Only show to Essentiel users

    return (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className={`bg-gradient-to-br from-[#0A0A0A] to-[#111111] border border-[#1C1C1C] p-6 ${className}`}
      >
        <div className="flex items-start justify-between gap-4">
          <div className="flex-1">
            <p 
              className="text-white text-base mb-2"
              style={{ fontFamily: "'Playfair Display', serif" }}
            >
              Tu as commencé quelque chose.
            </p>
            <p className="text-[#71717A] text-sm mb-4">
              Et si tu allais plus loin ?
            </p>
            
            <div className="space-y-1 mb-4">
              <p className="text-[#A1A1AA] text-xs">
                Contenu illimité. Direction claire. Accompagnement.
              </p>
            </div>
            
            <button
              onClick={handleUpgrade}
              className="text-[#D4AF37] text-sm hover:text-[#E5C585] transition-colors flex items-center gap-1"
            >
              Découvrir Vision Lab Expérience <ArrowRight size={14} />
            </button>
          </div>
          
          <button
            onClick={onClose}
            className="text-[#3F3F46] hover:text-[#52525B] transition-colors"
          >
            <X size={16} />
          </button>
        </div>
      </motion.div>
    );
  }

  return null;
};

export default ElevationMoment;
